import { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/context/auth";
import { useBranch } from "@/context/branch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BUSINESS_TYPES, type BusinessType } from "@/lib/business-types";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Settings as SettingsIcon } from "lucide-react";
import { OfflineSyncStatus } from "@/components/offline-sync-status";
import {
  LayoutDashboard,
  Package,
  Tags,
  ShoppingCart,
  ShoppingBag,
  ScanBarcode,
  PlusCircle,
  Truck,
  Store,
  Users,
  UserPlus,
  UserCheck,
  Receipt,
  History,
  Undo,
  BarChart3,
  ChevronDown,
  ChevronRight,
  LogOut,
  UserCircle,
  Shield,
  AlertTriangle,
  Clock,
  ClipboardList,
  Sparkles,
  Lock,
  Wallet,
  TrendingUp,
  Scale,
  ArrowLeftRight,
  Star,
  Landmark,
  Plus,
  Tag,
  MessageSquare,
  Globe,
  Ticket,
  Gift,
  GitBranch,
  ArrowRightLeft,
  Warehouse,
  Menu,
  GraduationCap,
  Megaphone,
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

type LockLevel = "pro" | "enterprise";

type NavLeaf = {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  lock?: LockLevel;
  permission?: string;
};

type NavGroup = {
  label: string;
  items: NavLeaf[];
};

type FlatSection = {
  kind: "flat";
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  lock?: LockLevel;
};

type CollapsibleSection = {
  kind: "collapsible";
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  lock?: LockLevel;
  items?: NavLeaf[];
  groups?: NavGroup[];
};

type NavSection = FlatSection | CollapsibleSection;

// ─── Nav definition ───────────────────────────────────────────────────────────

function filterSectionsForBusiness(
  sections: NavSection[],
  businessType: string | null | undefined,
  industryType: string | null | undefined,
): NavSection[] {
  const filterIndustryItems = (items: NavLeaf[]) => items.filter(item => {
    if (item.href === "/inventory?view=expiry" && !["supermarket", "food", "pharmacy"].includes(industryType ?? "")) return false;
    if (item.href === "/inventory?view=variants" && industryType !== "fashion") return false;
    return true;
  });

  if (!businessType || !(businessType in BUSINESS_TYPES)) {
    return sections.flatMap((section): NavSection[] => {
      if (section.id === "school-workspace") return [];
      if (section.id === "inventory" && section.kind === "collapsible" && section.items) {
        return [{ ...section, items: filterIndustryItems(section.items) }];
      }
      if (section.id === "people" && section.kind === "collapsible" && section.groups) {
        return [{ ...section, groups: section.groups.filter(group => group.label !== "School") }];
      }
      return [section];
    });
  }
  const m = BUSINESS_TYPES[businessType as BusinessType].modules;

  const filteredSections = sections.flatMap((section): NavSection[] => {
    if (section.id === "school-workspace" && businessType !== "school_campus") return [];
    if (section.id === "inventory") {
      if (!m.inventory) return [];
      if (section.kind === "collapsible" && section.items) {
        return [{ ...section, items: filterIndustryItems(section.items.filter(item => {
          if (item.href === "/purchases" && !m.purchases) return false;
          if (item.href === "/purchase-returns" && !m.purchases) return false;
          if (item.href === "/returns" && !m.returns) return false;
          return true;
        }))}];
      }
    }

    if (section.id === "people" && section.kind === "collapsible" && section.groups) {
      const filteredGroups = section.groups.flatMap((group): NavGroup[] => {
        if (group.label === "Suppliers" && !m.suppliers) return [];
        if (group.label === "School" && businessType !== "school_campus") return [];
        if (group.label === "Customers") {
          if (!m.customers) return [];
          return [{ ...group, items: group.items.filter(item => {
            if (item.href.includes("loyalty") && !m.loyalty) return false;
            return true;
          })}];
        }
        if (group.label === "Staff") {
          return [{ ...group, items: group.items.filter(item => {
            if (item.href === "/attendance" && !m.attendance) return false;
            return true;
          })}];
        }
        return [group];
      });
      return [{ ...section, groups: filteredGroups }];
    }

    if (section.id === "accounting" && !m.accounting) return [];

    if (section.id === "sales" && section.kind === "collapsible" && section.items) {
      return [{ ...section, items: section.items.filter(item => {
        if (item.href === "/gift-registry" && !m.giftRegistry) return false;
        return true;
      })}];
    }

    return [section];
  });

  if (businessType !== "school_campus") return filteredSections;

  const salesSection = filteredSections.find((section): section is CollapsibleSection =>
    section.id === "sales" && section.kind === "collapsible",
  );
  const inventorySection = filteredSections.find((section): section is CollapsibleSection =>
    section.id === "inventory" && section.kind === "collapsible",
  );
  const campusShopSection: CollapsibleSection = {
    kind: "collapsible",
    id: "campus-shop",
    label: "Campus Shop",
    icon: Store,
    groups: [
      { label: "Sales", items: salesSection?.items ?? [] },
      { label: "Products & stock", items: inventorySection?.items ?? [] },
    ].filter(group => group.items.length > 0),
  };

  return filteredSections.flatMap((section): NavSection[] => {
    if (section.id === "inventory" || section.id === "sales") return [];
    if (section.id === "school-workspace") return [section, campusShopSection];
    return [section];
  });
}

const ALL_SECTIONS: NavSection[] = [
  {
    kind: "flat",
    id: "dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
    href: "/",
  },
  {
    kind: "flat",
    id: "school-workspace",
    label: "School & Campus",
    icon: GraduationCap,
    href: "/school-fees",
  },
  {
    kind: "collapsible",
    id: "people",
    label: "People",
    icon: Users,
    groups: [
      {
        label: "Staff",
        items: [
          { name: "Staff List", href: "/staff", icon: Users, permission: "staff" },
          { name: "Add Staff", href: "/staff?add=1", icon: UserPlus, permission: "staff" },
          { name: "Clock-in / Clock-out", href: "/attendance", icon: Clock, lock: "enterprise", permission: "attendance" },
          { name: "Payroll", href: "/payroll", icon: Wallet, permission: "staff" },
          { name: "Performance Reports", href: "/people/staff-performance", icon: BarChart3, lock: "pro", permission: "staff" },
        ],
      },
      {
        label: "Customers",
        items: [
          { name: "Customer List", href: "/customers", icon: UserCheck, permission: "customers" },
          { name: "Add Customer", href: "/customers?add=1", icon: UserPlus, permission: "customers" },
          { name: "Customer Groups", href: "/customer-groups", icon: Users, permission: "customers" },
          { name: "Customer Invoices", href: "/customer-invoices", icon: AlertTriangle, permission: "customers" },
          { name: "Loyalty / Discounts", href: "/people/loyalty", icon: Star, lock: "enterprise", permission: "customers" },
          { name: "Customer Reports", href: "/people/customer-reports", icon: BarChart3, lock: "pro", permission: "customers" },
        ],
      },
      {
        label: "Suppliers",
        items: [
          { name: "Supplier List", href: "/suppliers", icon: Store, permission: "suppliers" },
          { name: "Add Supplier", href: "/suppliers?add=1", icon: Plus, permission: "suppliers" },
          { name: "Supplier Bills", href: "/supplier-bills", icon: Receipt, permission: "suppliers" },
          { name: "Supplier Ledger", href: "/supplier-ledger", icon: Wallet, permission: "suppliers" },
          { name: "Payments", href: "/accounting/payments", icon: Wallet, lock: "pro", permission: "accounting" },
          { name: "Performance Reports", href: "/people/supplier-reports", icon: BarChart3, lock: "pro", permission: "suppliers" },
        ],
      },
    ],
  },
  {
    kind: "collapsible",
    id: "inventory",
    label: "Inventory",
    icon: Package,
    items: [
      { name: "Products", href: "/inventory", icon: Package, permission: "inventory" },
      { name: "Stock History", href: "/stock-movements", icon: History, permission: "inventory" },
      { name: "Add Product", href: "/inventory?add=1", icon: Plus, permission: "inventory" },
      { name: "Expiry Dates", href: "/inventory?view=expiry", icon: Clock, permission: "inventory" },
      { name: "Size / Color Matrix", href: "/inventory?view=variants", icon: Package, permission: "inventory" },
      { name: "Categories", href: "/categories", icon: Tags, permission: "inventory" },
      { name: "Add Category", href: "/categories?add=1", icon: Plus, permission: "inventory" },
      { name: "Brands", href: "/brands", icon: Tag, permission: "brands" },
      { name: "Purchases", href: "/purchases", icon: Truck, permission: "purchases" },
      { name: "Purchase Returns", href: "/purchase-returns", icon: Undo, permission: "purchases" },
      { name: "Bundles / Combos", href: "/product-bundles", icon: Package, permission: "inventory" },
      { name: "Returns", href: "/returns", icon: Undo, permission: "returns" },
    ],
  },
  {
    kind: "collapsible",
    id: "sales",
    label: "Sales",
    icon: ShoppingCart,
    items: [
      { name: "Price Checker", href: "/sales/price-checker", icon: ScanBarcode, permission: "price_checker" },
      { name: "New Sale", href: "/sales/new", icon: PlusCircle, permission: "pos" },
      { name: "Sales History", href: "/sales", icon: Receipt, permission: "sales" },
      { name: "Gift Vouchers", href: "/vouchers", icon: Ticket, permission: "sales" },
      { name: "Gift Registry", href: "/gift-registry", icon: Gift, permission: "sales" },
      { name: "Shift Register", href: "/shifts", icon: Clock, permission: "pos" },
    ],
  },
  {
    kind: "flat",
    id: "payment-hub",
    label: "Payment Hub",
    icon: Wallet,
    href: "/payment-hub",
  },
  {
    kind: "flat",
    id: "sms",
    label: "Bulk SMS",
    icon: MessageSquare,
    href: "/sms",
  },
  {
    kind: "collapsible",
    id: "accounting",
    label: "Accounting",
    icon: Landmark,
    groups: [
      {
        label: "Expenses",
        items: [
          { name: "Expense List", href: "/expenses", icon: Receipt, permission: "expenses" },
          { name: "Add Expense", href: "/expenses?add=1", icon: Plus, permission: "expenses" },
          { name: "Expense Reports", href: "/accounting/expense-reports", icon: BarChart3, lock: "pro", permission: "expenses" },
        ],
      },
      {
        label: "Payments",
        items: [
          { name: "Record Payment", href: "/accounting/payments?add=1", icon: PlusCircle, permission: "accounting" },
          { name: "Payment History", href: "/accounting/payments", icon: Wallet, permission: "accounting" },
          { name: "Payment Reports", href: "/accounting/payment-reports", icon: BarChart3, lock: "pro", permission: "accounting" },
        ],
      },
      {
        label: "Financial Reports",
        items: [
          { name: "Profit & Loss", href: "/accounting/profit-loss", icon: TrendingUp, permission: "accounting" },
          { name: "Budgets vs Actuals", href: "/accounting/budgets", icon: BarChart3, lock: "pro", permission: "accounting" },
          { name: "Balance Sheet", href: "/accounting/balance-sheet", icon: Scale, lock: "enterprise", permission: "accounting" },
          { name: "Cash Flow", href: "/accounting/cash-flow", icon: ArrowLeftRight, lock: "pro", permission: "accounting" },
        ],
      },
      {
        label: "Reconciliation",
        items: [
          { name: "Bank Reconciliation", href: "/accounting/bank-reconciliation", icon: Landmark, permission: "accounting" },
        ],
      },
    ],
  },
  {
    kind: "collapsible",
    id: "branches",
    label: "Branches",
    icon: GitBranch,
    lock: "pro" as const,
    groups: [
      {
        label: "Locations",
        items: [
          { name: "All Branches", href: "/branches", icon: Warehouse, permission: "branches" },
          { name: "Add Branch", href: "/branches?add=1", icon: Plus, permission: "branches" },
        ],
      },
      {
        label: "Stock Movement",
        items: [
          { name: "Stock Transfers", href: "/stock-transfers", icon: ArrowRightLeft, permission: "branches" },
          { name: "New Transfer", href: "/stock-transfers?add=1", icon: Plus, permission: "branches" },
        ],
      },
    ],
  },
  {
    kind: "collapsible",
    id: "reports",
    label: "Reports",
    icon: BarChart3,
    items: [
      { name: "Reports Overview", href: "/reports", icon: BarChart3, permission: "reports" },
      { name: "Product Sales", href: "/reports/product-sales", icon: ShoppingBag, permission: "reports" },
    ],
  },
  {
    kind: "flat",
    id: "ecommerce",
    label: "Online Store",
    icon: Globe,
    href: "/ecommerce",
  },
];

// ─── Permission filter ─────────────────────────────────────────────────────────

const FLAT_SECTION_PERMISSIONS: Record<string, string> = {
  dashboard: "dashboard",
  "school-workspace": "school_fees",
  reports: "reports",
  "payment-hub": "payment_hub",
  sms: "sms",
};

function filterSectionsForPermissions(sections: NavSection[], permissions: string[], isAdmin: boolean): NavSection[] {
  if (isAdmin) return sections;
  const hasPerm = (p: string) => permissions.includes(p);

  return sections.flatMap((section): NavSection[] => {
    if (section.kind === "flat") {
      const perm = FLAT_SECTION_PERMISSIONS[section.id];
      if (perm && !hasPerm(perm)) return [];
      return [section];
    }

    if (section.items) {
      const filtered = section.items.filter(item => !item.permission || hasPerm(item.permission));
      if (filtered.length === 0) return [];
      return [{ ...section, items: filtered }];
    }

    if (section.groups) {
      const filteredGroups = section.groups.flatMap((group): NavGroup[] => {
        const filteredItems = group.items.filter(item => !item.permission || hasPerm(item.permission));
        if (filteredItems.length === 0) return [];
        return [{ ...group, items: filteredItems }];
      });
      if (filteredGroups.length === 0) return [];
      return [{ ...section, groups: filteredGroups }];
    }

    return [section];
  });
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function allLeavesOf(section: NavSection): NavLeaf[] {
  if (section.kind === "flat") return [];
  if (section.items) return section.items;
  if (section.groups) return section.groups.flatMap(g => g.items);
  return [];
}

function isActivePath(href: string, location: string): boolean {
  const path = href.split("?")[0];
  if (path === "/") return location === "/";
  return location === path || location.startsWith(path + "/");
}

function isSectionActive(section: NavSection, location: string): boolean {
  if (section.kind === "flat") return isActivePath(section.href, location);
  return allLeavesOf(section).some(item => isActivePath(item.href, location));
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function LeafLink({ item, location, onNavigate, storePlan }: { item: NavLeaf; location: string; onNavigate?: () => void; storePlan?: string }) {
  const active = isActivePath(item.href, location);

  const isLocked = (() => {
    if (!item.lock) return false;
    const plan = storePlan ?? "basic";
    if (item.lock === "pro") return plan !== "pro" && plan !== "enterprise";
    if (item.lock === "enterprise") return plan !== "enterprise";
    return false;
  })();

  if (isLocked) {
    return (
      <button
        onClick={() => { onNavigate?.(); window.location.href = "/subscribe"; }}
        title={`Upgrade to ${item.lock === "enterprise" ? "Enterprise" : "Pro"} to unlock ${item.name}`}
        className="w-full flex items-center justify-between px-3 py-2 rounded-md transition-colors text-sm text-sidebar-foreground/40 hover:bg-sidebar-accent/40 hover:text-sidebar-foreground/60"
      >
        <span className="flex items-center gap-2.5 min-w-0">
          <item.icon className="w-4 h-4 flex-shrink-0 opacity-50" />
          <span className="truncate opacity-70">{item.name}</span>
        </span>
        <Lock className="w-3 h-3 flex-shrink-0 text-sidebar-foreground/30 ml-1" />
      </button>
    );
  }

  return (
    <Link
      href={item.href}
      onClick={onNavigate}
      className={`flex items-center justify-between px-3 py-2 rounded-md transition-colors text-sm ${
        active
          ? "bg-sidebar-primary text-sidebar-primary-foreground font-medium"
          : "text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
      }`}
    >
      <span className="flex items-center gap-2.5 min-w-0">
        <item.icon className="w-4 h-4 flex-shrink-0" />
        <span className="truncate">{item.name}</span>
      </span>
    </Link>
  );
}

function CollapsibleNav({
  section,
  isOpen,
  onToggle,
  location,
  onNavigate,
  storePlan,
}: {
  section: CollapsibleSection;
  isOpen: boolean;
  onToggle: () => void;
  location: string;
  onNavigate?: () => void;
  storePlan?: string;
}) {
  const active = isSectionActive(section, location);

  // Determine if this section is locked for the current plan
  const isLocked = (() => {
    if (!section.lock) return false;
    if (section.lock === "enterprise") return storePlan !== "enterprise";
    if (section.lock === "pro") return storePlan !== "pro" && storePlan !== "enterprise";
    return false;
  })();

  const handleClick = () => {
    if (isLocked) {
      window.location.href = "/subscribe";
      onNavigate?.();
      return;
    }
    onToggle();
  };

  return (
    <div>
      <button
        onClick={handleClick}
        title={isLocked ? `Upgrade to ${section.lock === "enterprise" ? "Enterprise" : "Pro"} to unlock ${section.label}` : undefined}
        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-md transition-colors text-sm font-medium ${
          isLocked
            ? "text-sidebar-foreground/50 hover:bg-sidebar-accent/40 hover:text-sidebar-foreground/70"
            : active && !isOpen
            ? "bg-sidebar-accent text-sidebar-accent-foreground"
            : "text-sidebar-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground"
        }`}
      >
        <span className="flex items-center gap-3">
          <section.icon className={`w-5 h-5 ${isLocked ? "opacity-50" : ""}`} />
          <span className={isLocked ? "opacity-70" : ""}>{section.label}</span>
        </span>
        {isLocked ? (
          <Lock className="w-3.5 h-3.5 text-sidebar-foreground/40" />
        ) : isOpen ? (
          <ChevronDown className="w-4 h-4 text-sidebar-foreground/50" />
        ) : (
          <ChevronRight className="w-4 h-4 text-sidebar-foreground/50" />
        )}
      </button>

      {isOpen && !isLocked && (
        <div className="ml-2 mt-0.5 border-l border-sidebar-border/40 pl-2 space-y-0.5 mb-1">
          {section.items?.map(item => (
            <LeafLink key={item.href} item={item} location={location} onNavigate={onNavigate} storePlan={storePlan} />
          ))}

          {section.groups?.map(group => (
            <div key={group.label}>
              <div className="px-3 pt-3 pb-1">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-sidebar-foreground/40">
                  {group.label}
                </span>
              </div>
              <div className="space-y-0.5">
                {group.items.map(item => (
                  <LeafLink key={item.href} item={item} location={location} onNavigate={onNavigate} storePlan={storePlan} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Shared sidebar content ───────────────────────────────────────────────────

function SidebarContent({
  sections,
  openSections,
  toggle,
  location,
  isEnterprise,
  isAdmin,
  businessType,
  btConfig,
  user,
  store,
  trialDaysLeft,
  branches,
  activeBranchId,
  setActiveBranchId,
  hasBranches,
  logout,
  onNavigate,
}: {
  sections: NavSection[];
  openSections: Set<string>;
  toggle: (id: string) => void;
  location: string;
  isEnterprise: boolean;
  isAdmin: boolean;
  businessType: BusinessType | undefined;
  btConfig: (typeof BUSINESS_TYPES)[BusinessType] | undefined;
  user: any;
  store: any;
  trialDaysLeft: number;
  branches: any[];
  activeBranchId: number | null;
  setActiveBranchId: (id: number | null) => void;
  hasBranches: boolean;
  logout: () => Promise<boolean>;
  onNavigate?: () => void;
}) {
  const planAllows = (lock?: LockLevel) => {
    if (!lock) return true;
    const plan = store?.plan ?? "basic";
    if (lock === "pro") return plan === "pro" || plan === "enterprise";
    if (lock === "enterprise") return plan === "enterprise";
    return true;
  };

  const FlatLink = ({ href, label, icon: Icon, lock }: { href: string; label: string; icon: React.ComponentType<{ className?: string }>; lock?: LockLevel }) => {
    const active = isActivePath(href, location);
    const locked = !planAllows(lock);
    if (locked) {
      return (
        <button
          onClick={() => { onNavigate?.(); window.location.href = "/subscribe"; }}
          title={`Upgrade to ${lock === "enterprise" ? "Enterprise" : "Pro"} to unlock ${label}`}
          className="w-full flex items-center justify-between px-3 py-2.5 rounded-md transition-colors text-sm font-medium text-sidebar-foreground/50 hover:bg-sidebar-accent/40 hover:text-sidebar-foreground/70"
        >
          <span className="flex items-center">
            <Icon className="mr-3 h-5 w-5 opacity-50" />
            <span className="opacity-70">{label}</span>
          </span>
          <Lock className="w-3.5 h-3.5 text-sidebar-foreground/40" />
        </button>
      );
    }
    return (
      <Link
        href={href}
        onClick={onNavigate}
        className={`flex items-center px-3 py-2.5 rounded-md transition-colors text-sm font-medium ${
          active
            ? "bg-sidebar-primary text-sidebar-primary-foreground"
            : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        }`}
      >
        <Icon className="mr-3 h-5 w-5" />
        {label}
      </Link>
    );
  };

  return (
    <>
      {/* Branch switcher */}
      {hasBranches && (
        <div className="mx-3 mt-3 flex-shrink-0">
          <Select
            value={activeBranchId ? String(activeBranchId) : "all"}
            onValueChange={v => setActiveBranchId(v === "all" ? null : Number(v))}
          >
            <SelectTrigger className="h-8 text-xs bg-sidebar-accent border-sidebar-border text-sidebar-foreground w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Branches</SelectItem>
              {branches.map(b => (
                <SelectItem key={b.id} value={String(b.id)}>
                  {b.type === "warehouse" ? "🏭" : "🏪"} {b.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Trial banner */}
      {store?.subscriptionStatus === "trial" && (
        <Link href="/subscribe" onClick={onNavigate} className="mx-3 mt-3 block flex-shrink-0">
          <div className={`px-3 py-2 rounded-md text-xs flex items-center gap-2 transition-colors hover:opacity-90 ${
            trialDaysLeft <= 3
              ? "bg-red-500/20 border border-red-500/30 text-red-300"
              : "bg-indigo-500/15 border border-indigo-500/25 text-indigo-300"
          }`}>
            <AlertTriangle className="w-3 h-3 flex-shrink-0" />
            <span className="flex-1">
              {trialDaysLeft === 0
                ? "Trial expires today — upgrade now"
                : trialDaysLeft <= 3
                ? `${trialDaysLeft} day${trialDaysLeft === 1 ? "" : "s"} left — upgrade now`
                : `${trialDaysLeft} days left in trial`}
            </span>
          </div>
        </Link>
      )}

      {/* Scrollable nav */}
      <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
        {sections.map(section => {
          if (section.kind === "flat") {
            return <FlatLink key={section.id} href={section.href} label={section.label} icon={section.icon} lock={section.lock} />;
          }
          return (
            <CollapsibleNav
              key={section.id}
              section={section}
              isOpen={openSections.has(section.id)}
              onToggle={() => toggle(section.id)}
              location={location}
              onNavigate={onNavigate}
              storePlan={store?.plan}
            />
          );
        })}

        {(user?.role === "admin" || user?.role === "manager") && user?.businessType !== "school_campus" && (
          <FlatLink href="/partner-ads" label="Partner offers" icon={Megaphone} />
        )}

        {/* Enterprise separator */}
        <div className="pt-3 pb-1">
          <div className="flex items-center gap-1.5 px-3 mb-1">
            <span className="text-[10px] font-semibold text-sidebar-foreground/40 uppercase tracking-widest">Enterprise</span>
            {!isEnterprise && <Lock className="w-3 h-3 text-sidebar-foreground/40" />}
          </div>
          {(!businessType || BUSINESS_TYPES[businessType]?.modules.checklists) && (
            <FlatLink href="/checklists" label="Checklists" icon={ClipboardList} />
          )}
          <FlatLink href="/addons" label="Add-ons" icon={Sparkles} />
        </div>

        {isAdmin && (
          <FlatLink href="/admin" label="Admin" icon={Shield} />
        )}
        {isAdmin && (
          <div className="md:hidden">
            <FlatLink href="/subscribe" label="Manage Subscription" icon={CreditCardIcon} />
          </div>
        )}
      </nav>

      {/* User menu */}
      <div className="border-t border-sidebar-border p-3 flex-shrink-0">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-testid="button-profile-menu"
              className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-sidebar-accent transition-colors text-left"
            >
              <div className="w-8 h-8 bg-primary/30 rounded-full flex items-center justify-center flex-shrink-0">
                <UserCircle className="w-5 h-5 text-primary-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-sidebar-foreground/60 truncate">{store?.name}</p>
              </div>
              <ChevronDown className="w-4 h-4 text-sidebar-foreground/50 flex-shrink-0" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            side={onNavigate ? "top" : "bottom"}
            className="w-56 z-[100001]"
          >
            <DropdownMenuLabel>
              <div>
                <p className="font-medium">{user?.name}</p>
                <p className="text-xs text-muted-foreground font-normal">{user?.email}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="font-normal text-xs text-muted-foreground">
              {store?.name} · <span className="capitalize">{store?.plan}</span>
              {store?.subscriptionStatus === "trial" && (
                <Badge variant="secondary" className="ml-1 text-xs">Trial</Badge>
              )}
            </DropdownMenuLabel>
            {btConfig && (
              <>
                <DropdownMenuLabel className="font-normal text-xs">
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-muted text-muted-foreground border text-[11px]">
                    <span>{btConfig.emoji}</span>
                    <span>{btConfig.shortLabel}</span>
                  </span>
                </DropdownMenuLabel>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link href="/settings" onClick={onNavigate}>
                <SettingsIcon className="w-4 h-4 mr-2" />
                Settings
              </Link>
            </DropdownMenuItem>
            {isAdmin && <DropdownMenuItem asChild>
              <Link href="/subscribe" onClick={onNavigate}>
                <CreditCardIcon className="w-4 h-4 mr-2" />
                Manage Subscription
              </Link>
            </DropdownMenuItem>}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={async () => {
                try {
                  if (await logout()) window.location.href = "/login";
                } catch (error) {
                  window.alert(error instanceof Error ? error.message : "Could not sign out. Please try again.");
                }
              }}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );
}

// ─── Main Layout ──────────────────────────────────────────────────────────────

export function Layout({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const { user, store, logout, trialDaysLeft, hasActiveSubscription } = useAuth();
  const { branches, activeBranchId, setActiveBranchId, hasBranches } = useBranch();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isEnterprise = store?.plan === "enterprise";
  const isAdmin = user?.role === "admin" || user?.role === "superadmin";
  const businessType = user?.businessType as BusinessType | undefined;
  const userPermissions = user?.permissions ?? [];
  const industryType = store?.industryType ?? user?.businessType;
  const btConfig = businessType ? BUSINESS_TYPES[businessType] : undefined;

  const SECTIONS = useMemo(() => {
    const byBusiness = filterSectionsForBusiness(ALL_SECTIONS, businessType, industryType);
    const byPermission = filterSectionsForPermissions(byBusiness, userPermissions, isAdmin);
    if (businessType === "school_campus" && (isAdmin || userPermissions.includes("school_fees"))) {
      return byPermission.filter(section => section.id !== "dashboard");
    }
    return byPermission;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessType, industryType, isAdmin, JSON.stringify(userPermissions)]);

  // Sections that start open: those whose child is currently active
  const [openSections, setOpenSections] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    for (const s of ALL_SECTIONS) {
      if (s.kind === "collapsible" && isSectionActive(s, location)) {
        initial.add(s.id);
      }
    }
    return initial;
  });

  // Auto-expand active section when route changes; close mobile drawer
  useEffect(() => {
    setMobileOpen(false);
    setOpenSections(prev => {
      const next = new Set(prev);
      for (const s of SECTIONS) {
        if (s.kind === "collapsible" && isSectionActive(s, location)) {
          next.add(s.id);
        }
      }
      return next;
    });
  }, [location, SECTIONS]);

  const toggle = (id: string) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const sharedProps = {
    sections: SECTIONS,
    openSections,
    toggle,
    location,
    isEnterprise,
    isAdmin,
    businessType,
    btConfig,
    user,
    store,
    trialDaysLeft,
    branches,
    activeBranchId,
    setActiveBranchId,
    hasBranches,
    logout,
  };

  return (
    <div className="flex h-screen w-full overflow-hidden bg-background">

      {/* ── Desktop sidebar (hidden on mobile) ── */}
      <aside className="hidden md:flex w-64 bg-sidebar text-sidebar-foreground flex-col flex-shrink-0">
        {/* Logo */}
        <div className="h-16 flex items-center px-6 border-b border-sidebar-border font-bold text-xl tracking-tight text-white gap-2 flex-shrink-0">
          <img src={`${import.meta.env.BASE_URL}logo.png`} alt="RetailPulsePOS" className="w-8 h-8 rounded-md object-cover flex-shrink-0" />
          RetailPulsePOS
        </div>
        <SidebarContent {...sharedProps} />
      </aside>

      {/* ── Mobile drawer (Sheet) ── */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-72 bg-sidebar text-sidebar-foreground flex flex-col border-r-0">
          <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
          {/* Logo inside drawer */}
          <div className="h-16 flex items-center px-6 border-b border-sidebar-border font-bold text-xl tracking-tight text-white gap-2 flex-shrink-0">
            <img src={`${import.meta.env.BASE_URL}logo.png`} alt="RetailPulsePOS" className="w-8 h-8 rounded-md object-cover flex-shrink-0" />
            RetailPulsePOS
          </div>
          <SidebarContent {...sharedProps} onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* ── Main content area ── */}
      <main className="flex-1 flex flex-col overflow-hidden min-w-0">

        {/* Mobile top header */}
        <header className="md:hidden flex items-center gap-3 h-14 px-4 border-b bg-background flex-shrink-0">
          <button
            onClick={() => setMobileOpen(true)}
            className="p-2 -ml-2 rounded-md hover:bg-accent transition-colors"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 font-bold text-base tracking-tight">
            <img src={`${import.meta.env.BASE_URL}logo.png`} alt="RetailPulsePOS" className="w-6 h-6 rounded object-cover" />
            RetailPulsePOS
          </div>
        </header>

        {/* Trial warning bar */}
        {store?.subscriptionStatus === "trial" && trialDaysLeft <= 7 && trialDaysLeft > 3 && (
          <div className="bg-amber-50 border-b border-amber-200 px-4 md:px-6 py-2 flex items-center justify-between flex-shrink-0">
            <p className="text-sm text-amber-800 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span>Your free trial expires in {trialDaysLeft} days.</span>
            </p>
            <Link href="/subscribe">
              <Button size="sm" variant="outline" className="h-7 text-xs border-amber-300 text-amber-800 hover:bg-amber-100 flex-shrink-0">
                Choose a Plan
              </Button>
            </Link>
          </div>
        )}

        <OfflineSyncStatus />

        <div className="flex-1 overflow-auto p-4 md:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}

function CreditCardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <line x1="2" y1="10" x2="22" y2="10" />
    </svg>
  );
}
